import React, {useContext, useRef, useState} from 'react';
import TablePage from "../../../pages/tablePage/TablePage";
import {
    useCharacterPackage,
    useDeleteCharacterPackage,
    useSaveCharacterPackage
} from "./useCharacterPackage";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import {message, Switch, Table} from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import {getItemName} from "../../../shared/utils/basic";
import {AuthContext} from "../../../shared/contexts/authContext";
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from "../../probability/useItemData";
import CharacterPackageEditModal from "./CharacterPackageEditModal";
import {useReloadTime} from "../../serverInfo/useServerInfo";

const CharacterPackage = () => {
    const {currentUser} = useContext(AuthContext);

    const [rowData, setRowData] = useState({});
    const [csvData, setCsvData] = useState({});
    const {data: characterPackages, isLoading: isCharacterPackageLoading, error: characterPackageError, refetch} = useCharacterPackage();
    const {data: shopPackage, isLoading: isPackageLoading, error: packageError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isGroupLoading, error: groupError} = useShopPackageGroup();
    const {data: reloadTimeData, isLoading: isReloadTimeData, error: reloadTimeDataError, refetch: reloadTimeDataRefetch} = useReloadTime();
    const {data: krItemTextData, isLoading: isTextLoading, error: textError} = useItemKrTextData();

    const {mutate: saveCharacterPackage, isPending: isSaveCharacterPackagePending} = useSaveCharacterPackage();

    const refreshRef = useRef(null);

    const handleSwitchChange = async (checked, record) => {
        try {
            if (record.end < dayjs().unix()) {
                toast.error('종료된 패키지는 상태를 변경할 수 없습니다.');
                return;
            }

            record.onOff = checked ? 1 : 0;

            await saveCharacterPackage(record, {
                onSuccess: () => {
                    toast.dismiss();
                    toast.success('On/Off 상태가 성공적으로 변경되었습니다.');
                },
                onError: () => {
                    toast.dismiss();
                    toast.error('On/Off 상태 변경에 실패했습니다.');
                }
            });
            await refetch();
        } catch (e) {
            toast.error('On/Off 상태 변경에 실패했습니다.');
        }
    }

    const expandedRowRender = (record) => {
        try {
            return (
                <Table columns={expandColumns}
                       dataSource={Object.values(shopPackageGroup[currentUser.target])
                           .filter((group) => group.groupIdx === shopPackage[currentUser.target][record.index]?.groupIdx)
                           .map((group, index) => ({
                               ...group,
                               key: group.groupIdx + index,
                           }))}
                       pagination={false}
                />
            )
        } catch (e) {
            console.error(e);
            message.error(`패키지 그룹 데이터를 불러오는 중 에러가 발생했습니다.\n CSV 파일 및 서버 데이터를 확인해주세요.`);
        }
    };

    const columns = [
        {
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            search: true,
            sorter: (a, b) => a.seq - b.seq,
            width: 100,
        },
        {
            title: '패키지 인덱스',
            dataIndex: 'index',
            key: 'index',
            search: true,
            sorter: (a, b) => a.index - b.index,

        },
        {
            title: '패키지 이름',
            dataIndex: 'packageName',
            key: 'packageName',
            search: true
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            render: (_, record) => (
                <Switch
                    checked={record.onOff === 1}
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    onChange={checked => handleSwitchChange(checked, record)}
                    onClick={(_, e) => e.stopPropagation()}
                />
            ),
            filters: [
                {
                    text: 'On',
                    value: 1,
                },
                {
                    text: 'Off',
                    value: 0,
                },
            ],
        },
        {
            title: '시작 시간',
            dataIndex: 'begin',
            sorter: true,
            key: 'begin',
            render: (begin) => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '종료 시간',
            dataIndex: 'end',
            sorter: true,
            key: 'end',
            render: (end) => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: '패키지 별 운영기간(h)',
            dataIndex: 'packageTime',
            key: 'packageTime',
        },
        {
            title: '미니게임 인덱스',
            dataIndex: 'miniGameIdx',
            key: 'miniGameIdx',
        },
    ];

    const expandColumns = [
        {
            title: '인덱스',
            dataIndex: 'index',
            key: 'index',
        },
        {
            title: '상품이름',
            dataIndex: 'nameText',
            key: 'nameText',
        },
        {
            title: '인앱 인덱스',
            dataIndex: 'inAppIdx',
            key: 'inAppIdx',
        },
        {
            title: '다이아 가격',
            dataIndex: 'diaBuyValue',
            key: 'diaBuyValue',
        },
        {
            title: '보상 아이템',
            dataIndex: 'rewards',
            key: 'rewards',
            render: (rewards) => rewards.map((reward, idx) => (
                <div key={`${reward.id}-${idx}`}>
                    {getItemName(krItemTextData[currentUser.target], reward.id, reward.type)} x{reward.quantity}
                </div>
            )),
        },
    ];

    const props = {
        columns,
        localData: characterPackages,
        rowData,
        setRowData,
        refreshRef,
        tblData: csvData,
        setTblData: setCsvData,
        expandedRowRender,
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        modal: CharacterPackageEditModal,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch
    }

    const isLoading = isCharacterPackageLoading || isPackageLoading || isGroupLoading || isTextLoading || isReloadTimeData;
    const error = characterPackageError || packageError || groupError || textError || reloadTimeDataError;
    if (isLoading) return <IsLoading />;
    if (error) return <ErrorPage error={error} />;
    if (isSaveCharacterPackagePending) {
        toast.loading('캐릭터 패키지 저장중...');
    }

    return (
        <div>
            <TablePage {...props}/>
        </div>
    );
};

export default CharacterPackage;